import React, { Component } from 'react';
import classnames from 'classnames';

import { Row, Col, Input } from 'reactstrap';
import { Checkbox } from 'components/inputs';
import { NumberDisplay, FontAwesome } from 'components/ui';
import { MarkQueue, MarkDebouncer } from './marking';

import { toast } from 'react-toastify';
import { LEGACY_URL } from 'components/constants';

export class Task extends Component {

  static defaultProps = {
    task: {},
    user_id: 0
  }

  // a task owns every cell in its row, so the marking state lives here
  state = { pending: {}, waiting: {}, typed: {} }

  setFlag = field => ( key, on ) => this.setState( state => {
    const next = { ...state[ field ] };

    if ( on ) next[ key ] = true;
    else delete next[ key ];

    return { [ field ]: next };
  })

  queue = new MarkQueue( this.setFlag( 'pending' ) );

  debouncer = new MarkDebouncer( ( key, waiting ) => {
    this.setFlag( 'waiting' )( key, waiting );
    // once it is sent the store holds it again, so stop showing what was typed
    if ( !waiting ) this.setState( ({ typed }) => {
      const next = { ...typed };
      delete next[ key ];
      return { typed: next };
    });
  });

  componentWillUnmount() {
    this.queue.stop();
    // do not silently drop a number typed but not yet sent - SAVE_DELAY is
    // long enough that reloading the soldier can easily land inside it
    this.debouncer.stop();
  }

  markMission = ( key, dates, mark ) => {
    let { task, user_id } = this.props;

    return this.queue.run( key, () =>
      this.props.markMission( 
        [ user_id ], task.grid_id, dates, mark 
      )
      .catch( e => toast.error( e.message ) )
    );
  }

  toggleAll = e => {
    let marked = e.target.checked;
    let { date_task_marks } = this.props.task;
    let dates = date_task_marks
      .filter( mark => !!mark.marked !== marked )
      .map( mark => mark.mark_date );

    this.markMission( 'all', dates, marked );
  }

  onChange = mark_date => e => {
    let marked = e.target.checked;
    this.markMission( String( mark_date ), [ mark_date ], marked );
  }

  // quantities are held back while the number is still being typed
  onInputChange = ( mark_date ) => e => {
    const key = String( mark_date );
    const mark = e.target.value;

    this.setState( ({ typed }) => ({ typed: { ...typed, [ key ]: mark } }) );

    this.debouncer.hold( key, mark, value =>
      this.markMission( key, [ mark_date ], value )
    );
  }

  // leaving the box, or pressing enter, means the number is finished
  onInputBlur = mark_date => () => this.debouncer.flush( String( mark_date ) );

  onInputKeyDown = mark_date => e => {
    if ( e.key === 'Enter' ) this.debouncer.flush( String( mark_date ) );
  }

  // wrap a control so it can show its mark saving, or held back
  cell = ( mark_date, control ) => {
    const key = String( mark_date );
    const pending = !!this.state.pending[ key ];
    const waiting = !!this.state.waiting[ key ];

    return (
      <span className={ classnames( 'mark-cell', { pending, waiting } ) }>
        { control }
        { waiting && !pending && // typed, not sent yet - sits beside the box
          <span className='mark-cell-waiting' title='Waiting for you to finish typing'>
            <FontAwesome icon='pencil-alt' />
          </span>
        }
        { pending &&
          <span className='mark-cell-spinner'>
            <FontAwesome icon='circle-notch' spin />
          </span>
        }
      </span>
    );
  }

  // the box is bound to the store, which will not hold a held value yet
  typedValue = ( mark_date, stored ) => {
    const typed = this.state.typed[ String( mark_date ) ];
    return typed === undefined ? stored : typed;
  }

  render() {
    const { daily } = this.props;

    let { 
      short_name, task_name,  mandatory_qty,
      quantity,   points,     date_task_mark,
      subject_id, needed,     date_task_marks, 
      mark_date, start_date
    } = this.props.task;

    // remove more then one underscore from task details
    task_name = task_name.replace(/[_]{2,}/g, '').trim();
    // define some variables
    let earned, marked = false, disabled = false;
    // set the variables based on the task type
    if ( daily ) {
      earned = needed <= date_task_marks.filter( mark => !!mark.marked ).length;
      marked = date_task_marks.filter( mark => !mark.marked ).length === 0;
    } else if ( quantity ) {
      earned = date_task_mark.done_qty >= quantity;
    } else {
      earned = marked = !!date_task_mark.marked;
      disabled = !!date_task_mark.disable;
      if ( disabled ) {
        marked = true;
      }
    }

    let img_url = `${LEGACY_URL}/images/stickers/campaigns/${ subject_id }.gif`;
    if ( subject_id === 136 ) {
      img_url = `${LEGACY_URL}/images/stickers/campaigns/45.gif`;
    }

    return (
      <Row className='Task'>
        <Col lg={ daily ? 6 : 10 } md={ daily ? 6 : 9 } xs={ daily ? 12 : 9 }>
          <div className='cell info'>
            <img src={ img_url } 
              className={ earned ? 'earned animated tada' : 'unearned' } alt='sticker' />
            <div>
              <p className='short-name'>
                { short_name } { mandatory_qty >= 1 && <FontAwesome icon='star' /> }
              </p>
              <p className='task-name'>{ task_name }</p>
              <p className='miles'>
                <NumberDisplay value={ points } /> mile
                { points !== 1 ? 's' : '' }
                { daily ? '/day' : '' }
              </p>
            </div>
          </div>
        </Col>

        { daily &&
          <Col md={ 6 } className='days'>
            { !quantity && (
              <div className="cell all">
                { this.cell( 'all',
                  <Checkbox
                    checked={ marked }
                    disabled={ !!this.state.pending.all }
                    onChange={ this.toggleAll } />
                )}
              </div>
            )}
            { date_task_marks.map( ({ marked, mark_date, done_qty }, index) => 
              <div className="cell" key={ index }>
                { this.cell( mark_date, quantity && quantity > 0 ? (
                  <Input 
                    type='text'
                    max={ quantity }
                    placeholder={ quantity }
                    defaultValue={ done_qty || '' }
                    onChange={ this.onInputChange( mark_date ) }
                    onBlur={ this.onInputBlur( mark_date ) }
                    onKeyDown={ this.onInputKeyDown( mark_date ) } />
                ) : (
                  <Checkbox 
                    checked={ marked }
                    disabled={ !!this.state.pending[ mark_date ] }
                    onChange={ this.onChange( mark_date ) } />
                ))}
              </div>
            )}
            { date_task_marks.length < 7 && 
              [...Array( 7 - date_task_marks.length )].map( ( number, index ) =>
                <div className="cell" key={ index }>
                  <Checkbox disabled />
                </div>
            )}
          </Col>
        }

        { !daily && !quantity &&
          <Col xs={ 3 } lg={ 2 }>
            <div className="cell">
              { this.cell( mark_date || start_date,
                <Checkbox 
                  checked={ marked } 
                  disabled={ disabled || !!this.state.pending[ mark_date || start_date ] }
                  onChange={ this.onChange( mark_date || start_date ) } />
              )}
            </div>
          </Col>
        }
        { !daily && quantity &&
          <Col xs={ 3 } lg={ 2 }>
            <div className="cell">
              { this.cell( mark_date || start_date,
                <Input 
                  type='number'
                  placeholder={ quantity }
                  value={ this.typedValue(
                    mark_date || start_date, date_task_mark.done_qty || ''
                  )}
                  onChange={ this.onInputChange( mark_date || start_date ) }
                  onBlur={ this.onInputBlur( mark_date || start_date ) }
                  onKeyDown={ this.onInputKeyDown( mark_date || start_date ) } />
              )}
            </div>
          </Col>
        }
      </Row>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/missions/Task.jsx